import styled from 'styled-components';

export const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
`;

export const LoadingContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  z-index: 20;

  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  span {
    margin-left: 8px;
    font-size: 12px;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

export const AnnotationMarkers = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

// Vertical dotted line dropping from a point-in-time flag down through the plot.
export const VerticalDottedLine = styled.div`
  position: absolute;
  top: ${props => props.top || '0'};
  height: ${props => props.height || '100%'};
  left: ${props => props.left};
  transform: translateX(-50%);
  border-left: 1px dashed ${props => props.color};
  opacity: 0.35;
  pointer-events: none;
  z-index: 12;
`;

// Left-edge label pill for a range lane, e.g. "● Alarms 8". Sits over the
// plot's left margin so each lane is self-identifying.
export const LaneLabel = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: 4px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 8px 0 7px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${props => props.color};
  white-space: nowrap;
  pointer-events: none;
  z-index: 16;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    margin-right: 6px;
    border-radius: 50%;
    background: ${props => props.color};
  }
`;

// Faint full-extent track grounding a lane between its ranges.
export const LaneTrack = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  width: ${props => props.width};
  height: ${props => props.barHeight || '9px'};
  background: ${props => props.color};
  opacity: 0.08;
  border-radius: 999px;
  pointer-events: none;
  z-index: 11;
`;

// Solid "×N ▾" badge over an overlap zone; clicking it opens the range picker.
export const RangeBadge = styled.button`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border: 1px solid #fff;
  border-radius: 999px;
  background: ${props => props.color};
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 17;
`;

// Faint full-height tint showing a time-range annotation's span.
// Shown only while its lane bar is hovered, so overlapping ranges never
// stack into confusingly dark patches. Non-interactive (the bar handles hover).
export const RangeBandTint = styled.div`
  position: absolute;
  top: 0;
  height: ${props => props.height || '100%'};
  left: ${props => props.left};
  width: ${props => props.width};
  background: ${props => props.color};
  opacity: 0.16;
  border-left: 1px solid ${props => props.color};
  border-right: 1px solid ${props => props.color};
  pointer-events: none;
  z-index: 10;
`;

// One translucent pill per time-range annotation, packed into its type's lane.
// Density by opacity: overlapping pills alpha-blend, so concurrent ranges read
// darker without stacking into extra rows. Hover lifts a single pill to full
// strength and reveals its tooltip + span tint.
export const RangeBar = styled.div`
  position: absolute;
  top: ${props => props.top};
  height: ${props => props.barHeight || '9px'};
  left: ${props => props.left};
  width: ${props => props.width};
  min-width: 4px;
  background: ${props => props.color};
  opacity: ${props => (props.isHovered ? 0.9 : 0.34)};
  border-radius: 999px;
  pointer-events: auto;
  cursor: pointer;
  transition: opacity 0.15s ease;
  z-index: ${props => (props.isHovered ? 15 : 13)};

  &:hover {
    opacity: 0.9;
  }
`;

// Transparent layer that captures click/drag gestures while in add-note mode.
// Height is constrained to the trend plot so the context chart stays usable.
export const CreateCaptureLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.height || '100%'};
  pointer-events: auto;
  cursor: crosshair;
  z-index: 30;
`;

// Live selection rectangle drawn while dragging out a time-range
export const DragSelectionBand = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.left};
  width: ${props => props.width};
  background: #2cb34a;
  opacity: 0.18;
  border-left: 1px solid #2cb34a;
  border-right: 1px solid #2cb34a;
  pointer-events: none;
  z-index: 31;
`;

// Vertical guide line that follows the cursor while in add-note mode
export const HoverGuideLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${props => props.left};
  transform: translateX(-50%);
  border-left: 1px dashed #2cb34a;
  pointer-events: none;
  z-index: 32;
`;

// Floating label showing the timestamp at the cursor
export const HoverTimeLabel = styled.div`
  position: absolute;
  top: 4px;
  left: ${props => props.left};
  transform: ${props => (props.alignRight ? 'translateX(-100%)' : 'translateX(0)')};
  margin-left: ${props => (props.alignRight ? '-6px' : '6px')};
  padding: 2px 6px;
  background: #2cb34a;
  color: #fff;
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
  border-radius: 3px;
  pointer-events: none;
  z-index: 33;
`;
